import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowsClockwise, ArrowUp, ArrowDown, ArrowSquareOut,
  FunnelSimple, MagnifyingGlass, ChartLineUp, Globe, Vault,
  Bridge, CoinVertical, ArrowRight, Lightning, Timer, Lock, Percent,
  Detective, Eye
} from "@phosphor-icons/react";

function fmt(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(2)}`;
}

function PctChange({ val }) {
  if (val === null || val === undefined) return <span className="text-zinc-600">-</span>;
  const n = parseFloat(val);
  if (isNaN(n)) return <span className="text-zinc-600">-</span>;
  const isUp = n >= 0;
  return (
    <span className={`text-[10px] font-data flex items-center justify-end gap-0.5 ${isUp ? "text-emerald-400" : "text-red-400"}`}>
      {isUp ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
      {Math.abs(n).toFixed(2)}%
    </span>
  );
}

const CAT_COLORS = {
  "DEXes": "violet", "Lending": "blue", "Bridge": "cyan", "Liquid Staking": "emerald",
  "CDP": "amber", "Yield": "orange", "Derivatives": "red", "RWA": "teal",
};

function ProtocolRow({ proto }) {
  const catColor = CAT_COLORS[proto.category] || "zinc";
  const href = proto.url || `https://defillama.com/protocol/${proto.id}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      data-testid={`llama-row-${proto.rank}`}
      className="block w-full border-b border-zinc-800/30 hover:bg-zinc-900/40 transition-colors text-left">
      {/* Desktop table layout */}
      <div className="hidden md:grid grid-cols-[36px_1.5fr_120px_80px_80px_80px_80px_100px] items-center gap-2 px-3 py-2.5">
        <span className="text-[10px] font-data text-zinc-600 text-center">{proto.rank}</span>
        <div className="flex items-center gap-2 min-w-0">
          {proto.logo ? (
            <img src={proto.logo} alt={proto.name} className="w-6 h-6 rounded-full flex-shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0"><Vault size={12} className="text-zinc-500" /></div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-zinc-100 truncate max-w-[140px]">{proto.name}</span>
              {proto.symbol && <span className="text-[9px] text-zinc-500 font-data">{proto.symbol}</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <Badge className={`text-[6px] px-1 py-0 border-0 rounded-sm h-3 bg-${catColor}-900/30 text-${catColor}-400`}>{proto.category}</Badge>
              {proto.chain_count > 0 && (
                <span className="text-[8px] text-zinc-600 flex items-center gap-0.5"><Globe size={8} /> {proto.chain_count} chain{proto.chain_count > 1 ? "s" : ""}</span>
              )}
            </div>
          </div>
        </div>
        <span className="text-[11px] font-data text-zinc-200 text-right font-medium">{fmt(proto.tvl)}</span>
        <PctChange val={proto.tvl_change_1d} />
        <PctChange val={proto.tvl_change_7d} />
        <PctChange val={proto.tvl_change_1m} />
        <span className="text-[10px] font-data text-zinc-400 text-right">{proto.mcap ? fmt(proto.mcap) : "-"}</span>
        <div className="flex flex-wrap gap-0.5 justify-end">
          {proto.chains?.slice(0, 4).map(c => (<span key={c} className="text-[6px] px-1 py-0 bg-zinc-800/60 text-zinc-500 rounded-sm">{c}</span>))}
          {proto.chain_count > 4 && <span className="text-[6px] text-zinc-600">+{proto.chain_count - 4}</span>}
        </div>
      </div>

      {/* Mobile card layout */}
      <div className="md:hidden px-3 py-2.5">
        <div className="flex items-center gap-2.5 mb-2">
          <span className="text-[10px] font-data text-zinc-600 w-6 shrink-0 text-right">{proto.rank}</span>
          {proto.logo ? (
            <img src={proto.logo} alt={proto.name} className="w-7 h-7 rounded-full flex-shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0"><Vault size={14} className="text-zinc-500" /></div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-medium text-zinc-100 truncate">{proto.name}</span>
              {proto.symbol && <span className="text-[9px] text-zinc-500 font-data shrink-0">{proto.symbol}</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Badge className={`text-[7px] px-1 py-0 border-0 rounded-sm h-3 bg-${catColor}-900/30 text-${catColor}-400`}>{proto.category}</Badge>
              {proto.chain_count > 0 && (
                <span className="text-[8px] text-zinc-600 flex items-center gap-0.5"><Globe size={8} /> {proto.chain_count}</span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[12px] font-data font-medium text-zinc-100">{fmt(proto.tvl)}</div>
            <PctChange val={proto.tvl_change_1d} />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 pl-[42px]">
          <div>
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">7d</span>
            <PctChange val={proto.tvl_change_7d} />
          </div>
          <div>
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">1m</span>
            <PctChange val={proto.tvl_change_1m} />
          </div>
          <div>
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">MCap</span>
            <span className="text-[10px] font-data text-zinc-400">{proto.mcap ? fmt(proto.mcap) : "-"}</span>
          </div>
          <div className="flex flex-col items-end min-w-0">
            <span className="text-[8px] uppercase text-zinc-600 block leading-tight">Chains</span>
            <div className="flex gap-0.5 flex-wrap justify-end">
              {proto.chains?.slice(0, 2).map(c => (<span key={c} className="text-[6px] px-1 py-0 bg-zinc-800/60 text-zinc-500 rounded-sm truncate max-w-[50px]">{c}</span>))}
              {proto.chain_count > 2 && <span className="text-[6px] text-zinc-600">+{proto.chain_count - 2}</span>}
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}

/* ============================================================
   PROTOCOLS TAB (original view)
   ============================================================ */
function ProtocolsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/defillama-protocols`, { params: { category: catFilter } });
      setData(resp.data);
    } catch (e) { console.error("DeFiLlama fetch error:", e); }
    finally { setLoading(false); setRefreshing(false); }
  }, [catFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allProtocols = data?.protocols || [];
  const categories = data?.categories || [];
  const filtered = allProtocols.filter(p => {
    if (search) { const q = search.toLowerCase(); return p.name?.toLowerCase().includes(q) || p.symbol?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q); }
    return true;
  });
  const totalTvl = allProtocols.reduce((sum, p) => sum + (p.tvl || 0), 0);
  const TOP_CATS = ["all", "Lending", "Liquid Staking", "Bridge", "CDP", "Yield", "Derivatives", "RWA"];

  return (
    <div className="p-3 md:p-5 max-w-[1400px]" data-testid="llama-protocols-tab">
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4" data-testid="llama-summary">
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-2.5 md:p-3">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Total TVL (Shown)</span>
            <p className="text-sm md:text-base font-data font-bold text-zinc-100 mt-0.5">{fmt(totalTvl)}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-2.5 md:p-3">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Protocols Shown</span>
            <p className="text-sm md:text-base font-data font-bold text-zinc-100 mt-0.5">{allProtocols.length}</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-2.5 md:p-3 col-span-2 md:col-span-1">
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">Categories</span>
            <p className="text-sm md:text-base font-data font-bold text-zinc-100 mt-0.5">{categories.length}</p>
          </div>
        </div>
      )}
      <div className="flex items-center gap-1.5 md:gap-2 mb-3 overflow-x-auto scrollbar-hide" data-testid="llama-cat-filters">
        {TOP_CATS.map(cat => (
          <button key={cat} data-testid={`llama-cat-${cat}`} onClick={() => setCatFilter(cat)}
            className={`text-[10px] px-2.5 py-1 rounded-sm border transition-colors capitalize whitespace-nowrap shrink-0 ${catFilter === cat ? "bg-zinc-800 text-zinc-100 border-zinc-600" : "bg-zinc-900/30 text-zinc-500 border-zinc-800/60 hover:text-zinc-300"}`}>
            {cat === "all" ? "All Categories" : cat}
          </button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2" data-testid="llama-controls">
        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input data-testid="llama-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter protocols..."
            className="text-[10px] pl-7 pr-3 py-1.5 bg-zinc-900/50 border border-zinc-800/60 rounded-sm text-zinc-300 placeholder:text-zinc-600 outline-none focus:border-zinc-600 w-full sm:w-[220px]" />
        </div>
        <div className="flex items-center gap-3 justify-between sm:justify-end">
          <p className="text-[10px] text-zinc-600">{filtered.length} protocols</p>
          <button data-testid="refresh-llama-btn" onClick={() => fetchData(true)} disabled={refreshing}
            className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm">
            <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} /><span className="hidden sm:inline">{refreshing ? "Updating..." : "Refresh"}</span>
          </button>
        </div>
      </div>
      {loading && <div className="space-y-1">{[...Array(15)].map((_, i) => <Skeleton key={i} className="h-10 bg-zinc-900/50" />)}</div>}
      {!loading && filtered.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="llama-table">
          <div className="hidden md:grid grid-cols-[36px_1.5fr_120px_80px_80px_80px_80px_100px] items-center gap-2 px-3 py-2 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600">Protocol</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">TVL</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">1d</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">7d</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">1m</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">MCap</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Chains</span>
          </div>
          <div style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }} data-testid="llama-scroll">
            {filtered.map(proto => <ProtocolRow key={proto.id} proto={proto} />)}
          </div>
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-16"><Vault size={32} className="text-zinc-700 mx-auto mb-3" /><p className="text-sm text-zinc-500">No protocols match your filter</p></div>
      )}
      {data && (
        <div className="flex items-center justify-end mt-4 pt-3 border-t border-zinc-800/40">
          <p className="text-[9px] md:text-[10px] text-zinc-600 text-center md:text-right">Source: {data.data_source} | Updated: {new Date(data.fetched_at).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   BRIDGES TAB
   ============================================================ */
const BRIDGES_DATA = [
  {
    name: "PulseChain Bridge",
    url: "https://bridge.pulsechain.com",
    description: "The official PulseChain bridge for moving assets between Ethereum and PulseChain. Supports ERC-20 tokens, stablecoins, and native PLS bridging with low fees.",
    chains: ["Ethereum", "PulseChain"],
    color: "#22c55e",
    featured: true,
    features: [
      "Bridge ETH, USDC, USDT, DAI, and all ERC-20 tokens to PulseChain",
      "Low gas fees on the PulseChain side",
      "All Ethereum tokens were duplicated on PulseChain at launch (free copies)",
      "Validators secure bridged assets through the PulseChain consensus",
    ],
    howTo: [
      "Go to bridge.pulsechain.com",
      "Connect your wallet (MetaMask or Rabby recommended)",
      "Select the token and amount you want to bridge",
      "Choose direction: Ethereum to PulseChain or PulseChain to Ethereum",
      "Approve the token (first time only) and confirm the bridge transaction",
      "Wait for confirmation — Ethereum to PulseChain takes ~15 mins, reverse can take longer",
    ],
  },
];

function BridgesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBridges = async () => {
      try {
        const resp = await axios.get(`${API}/defillama-protocols`, { params: { category: "Bridge" } });
        setData(resp.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchBridges();
  }, []);

  const bridgeProtocols = data?.protocols || [];

  return (
    <div className="p-3 md:p-5 max-w-[1400px] space-y-4 md:space-y-6" data-testid="llama-bridges-tab">
      {/* PulseChain Bridge Featured Card */}
      {BRIDGES_DATA.map(bridge => (
        <div key={bridge.name} className="bg-zinc-900/50 border border-emerald-900/30 rounded-sm overflow-hidden" data-testid="pulsechain-bridge-card">
          <div className="p-5 border-b border-zinc-800/50">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${bridge.color}15` }}>
                  <Bridge size={20} weight="bold" style={{ color: bridge.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-heading font-bold text-zinc-50">{bridge.name}</h2>
                    <Badge className="text-[7px] px-1.5 py-0 border-0 bg-emerald-900/30 text-emerald-400 h-4">FEATURED</Badge>
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5">{bridge.description}</p>
                </div>
              </div>
              <a href={bridge.url} target="_blank" rel="noopener noreferrer" data-testid="pulsechain-bridge-link"
                className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-2 border border-emerald-800/40 rounded-sm hover:bg-emerald-900/20">
                <ArrowSquareOut size={12} /> Launch Bridge
              </a>
            </div>
            <div className="flex items-center gap-2 mt-3">
              {bridge.chains.map(c => (
                <span key={c} className="text-[9px] px-2 py-0.5 bg-zinc-800/60 text-zinc-400 rounded-sm">{c}</span>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-800/50">
            <div className="p-5">
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">Key Features</h3>
              <ul className="space-y-2.5">
                {bridge.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Lightning size={11} weight="bold" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-5">
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium">How to Bridge</h3>
              <ol className="space-y-2.5">
                {bridge.howTo.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-emerald-900/30 text-emerald-400 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{i + 1}</span>
                    <span className="text-xs text-zinc-300 leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      ))}

      {/* DeFiLlama Bridge Protocols */}
      <div>
        <h3 className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3 font-medium">All Bridge Protocols (DeFiLlama)</h3>
        {loading && <div className="space-y-1">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10 bg-zinc-900/50" />)}</div>}
        {!loading && bridgeProtocols.length > 0 && (
          <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden">
            <div className="hidden md:grid grid-cols-[36px_1.5fr_120px_80px_80px_80px_80px_100px] items-center gap-2 px-3 py-2 border-b border-zinc-800/60 bg-zinc-900/50">
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-center">#</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600">Protocol</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">TVL</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">1d</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">7d</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">1m</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">MCap</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Chains</span>
            </div>
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {bridgeProtocols.map(proto => <ProtocolRow key={proto.id} proto={proto} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   ASSET STAKING TAB — HEX on PulseChain & Ethereum
   ============================================================ */
const HEX_ASSETS = [
  {
    id: "hex-pulsechain",
    name: "HEX on PulseChain",
    symbol: "pHEX",
    chain: "PulseChain",
    chainColor: "#22c55e",
    url: "https://go.hex.com",
    logo: "https://assets.coingecko.com/coins/images/10103/small/HEX-logo.png",
    description: "HEX is a certificate of deposit on the blockchain. On PulseChain, HEX operates with extremely low gas fees, making staking accessible to everyone. PulseChain HEX (pHEX) is the native copy that was created at PulseChain launch.",
    stakingInfo: {
      mechanism: "Time-locked staking (T-Shares)",
      minStake: "No minimum — stake any amount of HEX",
      lockPeriod: "1 day to 5,555 days (15.2 years max)",
      rewards: "HEX inflation rewards distributed to stakers — longer stakes and bigger stakes earn more T-Shares",
      penalty: "Emergency End Stake penalty for ending early — you lose a portion of your principal and rewards",
    },
    howToStake: [
      "Go to go.hex.com and connect your wallet (MetaMask/Rabby on PulseChain network)",
      "Make sure you have pHEX tokens and PLS for gas fees",
      "Enter the amount of HEX you want to stake",
      "Choose your stake length (longer = more T-Shares = bigger rewards)",
      "Click 'Stake' and confirm the transaction in your wallet",
      "Your HEX is now locked and earning rewards daily until your stake end date",
      "On your end date, click 'End Stake' to collect your principal + rewards",
    ],
    tips: [
      "Longer stakes earn exponentially more T-Shares (the Longer Pays Better bonus)",
      "Bigger stakes also earn a bonus (the Bigger Pays Better bonus)",
      "Never emergency end stake early — the penalty can be severe",
      "Gas fees on PulseChain are fractions of a penny, making small stakes viable",
      "You can have multiple stakes with different end dates for flexibility",
    ],
  },
  {
    id: "hex-ethereum",
    name: "HEX on Ethereum",
    symbol: "eHEX",
    chain: "Ethereum",
    chainColor: "#627eea",
    url: "https://go.hex.com",
    logo: "https://assets.coingecko.com/coins/images/10103/small/HEX-logo.png",
    description: "The original HEX contract deployed on Ethereum. Functions identically to pHEX but with Ethereum gas fees. HEX on Ethereum (eHEX) is the OG version launched by Richard Heart in 2019.",
    stakingInfo: {
      mechanism: "Time-locked staking (T-Shares)",
      minStake: "No minimum — but consider Ethereum gas fees for profitability",
      lockPeriod: "1 day to 5,555 days (15.2 years max)",
      rewards: "Same T-Share reward system as PulseChain — inflation distributed proportionally to stakers",
      penalty: "Same emergency end stake penalty system — ending early costs you",
    },
    howToStake: [
      "Go to go.hex.com and connect your wallet (MetaMask on Ethereum mainnet)",
      "Make sure you have eHEX tokens and ETH for gas fees",
      "Enter the amount of HEX you want to stake",
      "Choose your stake length (1 to 5,555 days)",
      "Click 'Stake' and confirm the transaction — note: Ethereum gas can be $5–$50+",
      "Your HEX is now locked and earning T-Share rewards daily",
      "On your end date, click 'End Stake' to collect everything",
    ],
    tips: [
      "Gas fees on Ethereum can be significant — stake larger amounts to make fees worthwhile",
      "Check gas prices (use etherscan.io/gastracker) and stake when gas is low (weekends, off-hours)",
      "eHEX and pHEX are separate assets with separate prices — they are NOT interchangeable",
      "The same T-Share mechanics apply: Longer Pays Better, Bigger Pays Better",
      "Consider staking on PulseChain instead if gas fees are a concern",
    ],
  },
];

function AssetStakingTab() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="llama-staking-tab">
      <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-sm p-4 mb-2">
        <div className="flex items-center gap-2 mb-2">
          <Lock size={14} weight="bold" className="text-violet-400" />
          <span className="text-xs font-medium text-zinc-200">What is HEX Staking?</span>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          HEX staking locks your tokens for a chosen period in exchange for T-Shares. T-Shares earn you a portion of daily HEX inflation.
          The longer and larger your stake, the more T-Shares you receive. Think of it like a blockchain certificate of deposit — you commit time, you earn yield.
        </p>
      </div>

      {HEX_ASSETS.map(asset => (
        <div key={asset.id} className="bg-zinc-900/50 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid={`staking-card-${asset.id}`}>
          {/* Header */}
          <div className="p-5 border-b border-zinc-800/50">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <img src={asset.logo} alt={asset.name} className="w-10 h-10 rounded-full" />
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-heading font-bold text-zinc-50">{asset.name}</h2>
                    <Badge className="text-[8px] px-1.5 py-0 border-0 h-4" style={{ backgroundColor: `${asset.chainColor}20`, color: asset.chainColor }}>{asset.chain}</Badge>
                    <span className="text-[10px] text-zinc-500 font-data">{asset.symbol}</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">{asset.description}</p>
                </div>
              </div>
              <a href={asset.url} target="_blank" rel="noopener noreferrer" data-testid={`staking-launch-${asset.id}`}
                className="flex items-center gap-1.5 text-[11px] font-medium text-violet-400 hover:text-violet-300 transition-colors px-3 py-2 border border-violet-800/40 rounded-sm hover:bg-violet-900/20">
                <ArrowSquareOut size={12} /> Stake HEX
              </a>
            </div>
          </div>

          {/* Staking Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-800/40 border-b border-zinc-800/50">
            {Object.entries(asset.stakingInfo).map(([key, val]) => (
              <div key={key} className="p-3.5">
                <p className="text-[8px] uppercase tracking-wider text-zinc-600 mb-1">{key.replace(/([A-Z])/g, ' $1').replace('_', ' ')}</p>
                <p className="text-[10px] text-zinc-300 leading-relaxed">{val}</p>
              </div>
            ))}
          </div>

          {/* How To + Tips */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-zinc-800/50">
            <div className="p-5">
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium flex items-center gap-1.5">
                <CoinVertical size={11} weight="bold" className="text-violet-400" /> How to Stake
              </h3>
              <ol className="space-y-2.5">
                {asset.howToStake.map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-5 h-5 rounded-full bg-violet-900/30 text-violet-400 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{i + 1}</span>
                    <span className="text-xs text-zinc-300 leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="p-5">
              <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3 font-medium flex items-center gap-1.5">
                <Lightning size={11} weight="bold" className="text-amber-400" /> Tips
              </h3>
              <ul className="space-y-2.5">
                {asset.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <ArrowRight size={10} weight="bold" className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-zinc-300 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   PRIVACY PROTOCOLS TAB
   ============================================================ */
function PrivacyTab() {
  const protocols = [
    {
      name: "Tornado Cash",
      symbol: "TORN",
      chain: "Ethereum",
      chainColor: "#627EEA",
      category: "Mixer",
      description: "Decentralized, non-custodial privacy solution built on Ethereum. Uses zero-knowledge proofs (zk-SNARKs) to break the on-chain link between sender and receiver addresses. Deposit ETH or tokens, withdraw from a different address with no traceable connection.",
      url: "https://tornadocash.eth.limo",
      features: ["zk-SNARK proofs", "Non-custodial", "Multi-denomination pools", "ERC-20 support"],
      status: "Sanctioned by OFAC (Aug 2022) — smart contracts still operational on-chain",
    },
    {
      name: "Railgun",
      symbol: "RAIL",
      chain: "Multi-chain",
      chainColor: "#8B5CF6",
      category: "Privacy System",
      description: "On-chain privacy system that lets you use DeFi privately. Shield your tokens into the Railgun smart contract, then swap, lend, provide liquidity — all without revealing your wallet address. Uses zk-SNARKs for full transaction privacy within DeFi.",
      url: "https://railgun.org",
      features: ["Private DeFi transactions", "zk-SNARKs", "Private swaps via 0x/1inch", "Multi-chain (ETH, BSC, Polygon, Arbitrum)"],
      status: "Active — widely used for private DeFi interactions",
    },
    {
      name: "Aztec Network",
      symbol: "AZTEC",
      chain: "Ethereum L2",
      chainColor: "#627EEA",
      category: "Privacy L2",
      description: "Encrypted Layer 2 rollup on Ethereum. All transactions are private by default using zk-SNARKs. Supports private smart contracts (called 'Noir' programs) — meaning not just transfers but entire DeFi applications can run with full privacy.",
      url: "https://aztec.network",
      features: ["Private by default L2", "zk-SNARK rollup", "Noir smart contracts", "Private DeFi composability"],
      status: "Testnet — mainnet launch expected 2025-2026",
    },
    {
      name: "Secret Network",
      symbol: "SCRT",
      chain: "Cosmos / IBC",
      chainColor: "#2BCB8C",
      category: "Privacy L1",
      description: "Layer 1 blockchain with encrypted smart contracts ('Secret Contracts'). All computation is private — validators process encrypted data inside TEEs (Trusted Execution Environments) and never see the underlying data. Supports private tokens, private DeFi, and private NFTs.",
      url: "https://scrt.network",
      features: ["Encrypted smart contracts", "TEE-based privacy", "IBC interoperability", "Secret tokens (SNIP-20)"],
      status: "Active — live mainnet with ecosystem of private dApps",
    },
    {
      name: "Monero",
      symbol: "XMR",
      chain: "Monero",
      chainColor: "#FF6600",
      category: "Privacy Coin",
      description: "The gold standard of privacy cryptocurrencies. Every transaction is private by default — ring signatures hide the sender, stealth addresses hide the receiver, and RingCT hides the amount. No transparent transactions exist. Unlike Bitcoin where everything is visible, Monero is opaque by design.",
      url: "https://getmonero.org",
      features: ["Ring signatures", "Stealth addresses", "RingCT (hidden amounts)", "Default privacy — no opt-in"],
      status: "Active — most widely used privacy cryptocurrency since 2014",
    },
    {
      name: "Zcash",
      symbol: "ZEC",
      chain: "Zcash",
      chainColor: "#F4B728",
      category: "Privacy Coin",
      description: "Uses zk-SNARKs (which were pioneered for Zcash) to enable fully shielded transactions where sender, receiver, and amount are all encrypted on-chain. Offers both transparent and shielded transaction types — users choose their privacy level.",
      url: "https://z.cash",
      features: ["zk-SNARKs (pioneered here)", "Shielded pools", "Optional transparency", "Unified addresses"],
      status: "Active — ongoing development with focus on shielded-by-default",
    },
    {
      name: "Panther Protocol",
      symbol: "ZKP",
      chain: "Multi-chain",
      chainColor: "#00D395",
      category: "Privacy DeFi",
      description: "Decentralized protocol for private, interoperable DeFi. Users deposit assets into 'Shielded Pools' and receive zAssets (shielded versions). These zAssets can be used across DeFi while maintaining privacy. Includes a built-in private DEX and compliance-ready selective disclosure.",
      url: "https://pantherprotocol.io",
      features: ["Multi-Asset Shielded Pools", "zAssets for private DeFi", "Selective disclosure (compliance)", "Cross-chain privacy"],
      status: "Active — building towards multi-chain privacy DeFi",
    },
    {
      name: "Penumbra",
      symbol: "UM",
      chain: "Cosmos / IBC",
      chainColor: "#8B5CF6",
      category: "Privacy DEX",
      description: "Shielded DEX and staking chain in the Cosmos ecosystem. All transactions, swaps, staking delegations, and IBC transfers are private by default. Uses a novel 'multi-asset shielded pool' where all assets share one anonymity set.",
      url: "https://penumbra.zone",
      features: ["Private DEX (ZSwap)", "Private staking", "Private IBC transfers", "Multi-asset shielded pool"],
      status: "Active — mainnet launched 2024",
    },
    {
      name: "Oasis Network",
      symbol: "ROSE",
      chain: "Oasis",
      chainColor: "#0092F6",
      category: "Confidential L1",
      description: "Layer 1 blockchain designed for confidential smart contracts. The Sapphire ParaTime enables EVM-compatible confidential computing — Solidity developers can build private dApps with minimal changes. Uses TEEs for data confidentiality.",
      url: "https://oasisprotocol.org",
      features: ["Confidential EVM (Sapphire)", "TEE-based privacy", "Data tokenization", "EVM compatible"],
      status: "Active — mainnet live with confidential ParaTime",
    },
    {
      name: "Firo",
      symbol: "FIRO",
      chain: "Firo",
      chainColor: "#9B1C2E",
      category: "Privacy Coin",
      description: "Privacy-focused cryptocurrency using the Lelantus Spark protocol. Provides full transaction privacy with hidden sender, receiver, and amounts. Burn-and-redeem model — you burn coins to break the transaction trail and redeem new coins with no history.",
      url: "https://firo.org",
      features: ["Lelantus Spark protocol", "Burn-and-redeem privacy", "No trusted setup required", "Masternode network"],
      status: "Active — Spark protocol live on mainnet",
    },
    {
      name: "Namada",
      symbol: "NAM",
      chain: "Cosmos / IBC",
      chainColor: "#FFFF00",
      category: "Privacy L1",
      description: "Multi-asset, multi-chain shielded transfers. Built for the Cosmos ecosystem with IBC integration. Any asset from any IBC chain can be shielded in Namada's unified multi-asset shielded pool — the larger the pool, the stronger the privacy for everyone.",
      url: "https://namada.net",
      features: ["MASP (Multi-Asset Shielded Pool)", "IBC interoperability", "Shielded actions", "Public goods funding"],
      status: "Active — mainnet launched 2024",
    },
    {
      name: "Manta Network",
      symbol: "MANTA",
      chain: "Polkadot / L2",
      chainColor: "#2196F3",
      category: "Privacy L2",
      description: "Modular blockchain for zero-knowledge applications. Manta Pacific is an EVM-compatible L2 with zkSBT (zero-knowledge Soulbound Tokens) and private identity features. Offers universal circuits for ZK application development.",
      url: "https://manta.network",
      features: ["ZK-enabled L2", "zkSBT (private identity)", "Universal Circuits", "EVM compatible"],
      status: "Active — Manta Pacific L2 live on mainnet",
    },
  ];

  const catColors = {
    "Mixer": "violet", "Privacy System": "indigo", "Privacy L2": "blue", "Privacy L1": "emerald",
    "Privacy Coin": "orange", "Privacy DeFi": "teal", "Privacy DEX": "cyan", "Confidential L1": "sky",
  };

  return (
    <div className="p-5 max-w-[1400px] space-y-5" data-testid="privacy-tab">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-violet-900/20 flex items-center justify-center">
            <Detective size={20} weight="bold" className="text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">Privacy Protocols</h2>
            <p className="text-xs text-zinc-400">Protocols and blockchains focused on transaction privacy and data confidentiality</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          On most blockchains, every transaction is public — anyone can see your wallet balance, who you transact with, and how much you sent. Privacy protocols use cryptographic techniques like zero-knowledge proofs (zk-SNARKs), ring signatures, and trusted execution environments (TEEs) to shield this information while maintaining verifiability. Financial privacy is not about hiding — it's about having the same rights on-chain that you have with cash in your pocket.
        </p>
      </div>

      {/* Protocol cards */}
      <div className="space-y-3">
        {protocols.map((p, i) => {
          const cc = catColors[p.category] || "zinc";
          return (
            <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid={`privacy-protocol-${i}`}>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-heading font-bold text-zinc-100">{p.name}</h3>
                      <span className="text-[10px] text-zinc-500 font-data">{p.symbol}</span>
                      <Badge className={`text-[7px] px-1 py-0 border-0 h-3.5 bg-${cc}-900/30 text-${cc}-400`}>{p.category}</Badge>
                      <Badge className="text-[7px] px-1 py-0 border-0 h-3.5" style={{ backgroundColor: `${p.chainColor}20`, color: p.chainColor }}>{p.chain}</Badge>
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed max-w-3xl">{p.description}</p>
                  </div>
                  <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`privacy-link-${i}`}
                    className="flex items-center gap-1.5 text-[10px] font-medium text-violet-400 hover:text-violet-300 transition-colors px-2.5 py-1.5 border border-violet-800/40 rounded-sm hover:bg-violet-900/20 flex-shrink-0 whitespace-nowrap">
                    <ArrowSquareOut size={11} /> Visit Site
                  </a>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {p.features.map((f, fi) => (
                    <span key={fi} className="text-[9px] text-zinc-500 bg-zinc-950/50 border border-zinc-800/40 px-2 py-0.5 rounded-sm">{f}</span>
                  ))}
                </div>
                <div className="mt-2.5 flex items-center gap-1.5">
                  <Eye size={10} weight="bold" className="text-zinc-600" />
                  <span className="text-[9px] text-zinc-500">{p.status}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom note */}
      <div className="bg-violet-900/10 border border-violet-800/30 rounded-sm p-5" data-testid="privacy-bottom-note">
        <h3 className="text-xs font-medium text-violet-300 mb-2 flex items-center gap-2">
          <Lock size={14} weight="bold" /> Why Privacy Matters
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed">
          <strong className="text-zinc-100">Financial privacy is a fundamental right, not a crime.</strong> You don't show your bank statement to strangers — you shouldn't have to on-chain either. Privacy protocols protect individuals from surveillance, targeted attacks (hackers can see your wallet balance), business competitors, and authoritarian governments. Without privacy, public blockchains are worse than banks for personal financial surveillance — at least banks don't let the entire world see your transactions.
        </p>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE — Tabbed Layout
   ============================================================ */
const SUB_TABS = [
  { key: "protocols", label: "Protocols", icon: Vault },
  { key: "bridges", label: "Bridges", icon: Bridge },
  { key: "staking", label: "Asset Staking", icon: CoinVertical },
  { key: "privacy", label: "Privacy", icon: Detective },
];

export default function DefiLlamaPage() {
  const [tab, setTab] = useState("protocols");

  return (
    <div data-testid="defillama-page">
      <div className="sticky top-0 z-20 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/60 px-3 md:px-5 pt-3 md:pt-4 pb-0">
        <div className="flex items-center justify-between mb-2 md:mb-3">
          <div className="flex items-center gap-2 md:gap-2.5 min-w-0">
            <ChartLineUp size={20} weight="bold" className="text-blue-400 shrink-0" />
            <h1 className="font-heading text-lg md:text-2xl font-bold text-zinc-50 tracking-tight">DeFi Llama</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-blue-900/30 text-blue-400 h-4">TVL RANKINGS</Badge>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-9 bg-zinc-900/60 border border-zinc-800/60 p-0.5 gap-0.5 overflow-x-auto scrollbar-hide max-w-full flex justify-start" data-testid="llama-tabs">
            {SUB_TABS.map(t => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`llama-tab-${t.key}`}
                  className="text-[11px] px-3 py-1.5 h-8 gap-1.5 font-medium transition-colors whitespace-nowrap shrink-0 data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-50 data-[state=active]:shadow-sm">
                  <Icon size={13} weight="bold" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <div data-testid={`llama-content-${tab}`}>
        {tab === "protocols" && <ProtocolsTab />}
        {tab === "bridges" && <BridgesTab />}
        {tab === "staking" && <AssetStakingTab />}
        {tab === "privacy" && <PrivacyTab />}
      </div>
    </div>
  );
}
